// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FontAwesomeDetectorService, FontAwesomeFallbackService } from '@services';

interface IconFallbackState {
  element: HTMLElement;
  originalClasses: string[];
  originalContent: string;
  originalTextContent: string;
  fallbackClass: string;
  fallbackApplied: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FontAwesomeAutoFallbackService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private iconStates = new Map<HTMLElement, IconFallbackState>();
  private isActive = false;
  private observer: MutationObserver | null = null;

  constructor(
    private fontAwesomeDetector: FontAwesomeDetectorService,
    private fallbackService: FontAwesomeFallbackService
  ) {
    this.initializeAutoFallback();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopObserver();
  }

  private initializeAutoFallback(): void {
    // Listen to Font Awesome status changes
    this.fontAwesomeDetector.fontAwesomeLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoaded => {
        if (isLoaded) {
          this.restoreAllIcons();
          this.stopObserver();
        } else {
          this.loadFallbackAndApply();
        }
      });

    // Check current status in case detection already completed
    if (this.fontAwesomeDetector.isCheckPerformed()) {
      const isLoaded = this.fontAwesomeDetector.isFontAwesomeLoaded();
      if (!isLoaded) {
        this.loadFallbackAndApply();
      }
    }
  }

  private async loadFallbackAndApply(): Promise<void> {
    try {
      // Load the public Font Awesome CDN
      await this.fallbackService.loadPublicFontAwesome();
      
      // Apply fallback to all icons
      this.applyFallbackToAllIcons();
      this.startObserver();
    } catch (error) {
      // console.error('FontAwesome Auto Fallback: Failed to load public CDN, skipping icon fallback');
    }
  }

  private startObserver(): void {
    if (this.observer || typeof document === 'undefined') return;

    // Watch for new Font Awesome icons being added to the DOM
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            this.processElement(element);
            // Also check child elements
            this.processElementChildren(element);
          }
        });
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private stopObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private applyFallbackToAllIcons(): void {
    if (typeof document === 'undefined') return;

    // console.log('FontAwesome Auto Fallback: Scanning for Font Awesome icons...');
    
    // Find all elements with Font Awesome classes
    const selectors = [
      '.fas', '.far', '.fab', '.fal', '.fad', // Font Awesome prefix classes
      '[class*="fa-"]' // Any class containing fa-
    ];

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        this.processElement(element as HTMLElement);
      });
    });

    // console.log(`FontAwesome Auto Fallback: Processed ${this.iconStates.size} icons`);
  }

  private processElement(element: HTMLElement): void {
    if (!element || this.iconStates.has(element)) return;

    const classes = Array.from(element.classList);
    const faClasses = this.fallbackService.extractFontAwesomeClasses(classes.join(' '));
    
    if (faClasses.length === 0) return; // Not a Font Awesome icon

    // Skip if element already has emoji content or is not an icon element
    const tagName = element.tagName.toLowerCase();
    if (!['i', 'span', 'em'].includes(tagName)) return;

    // Store original state
    const iconState: IconFallbackState = {
      element,
      originalClasses: [...classes],
      originalContent: element.innerHTML,
      originalTextContent: element.textContent || '',
      fallbackClass: this.fallbackService.getFallbackClassForClasses(classes),
      fallbackApplied: false
    };

    this.iconStates.set(element, iconState);

    // Apply fallback if Font Awesome is not loaded
    if (!this.fontAwesomeDetector.isFontAwesomeLoaded()) {
      this.applyFallbackToIcon(iconState);
    }
  }

  private processElementChildren(parent: HTMLElement): void {
    const selectors = [
      '.fas', '.far', '.fab', '.fal', '.fad',
      '[class*="fa-"]'
    ];

    selectors.forEach(selector => {
      const elements = parent.querySelectorAll(selector);
      elements.forEach((element) => {
        this.processElement(element as HTMLElement);
      });
    });
  }

  private applyFallbackToIcon(iconState: IconFallbackState): void {
    if (iconState.fallbackApplied) return;

    const element = iconState.element;
    
    // Clear existing content
    element.textContent = '';
    
    // Add fallback identification class
    element.classList.add('fa-fallback-applied');
    
    // Remove original Font Awesome classes
    const faClasses = this.fallbackService.extractFontAwesomeClasses(iconState.originalClasses.join(' '));
    faClasses.forEach(faClass => {
      element.classList.remove(faClass);
    });

    // Apply fallback Font Awesome classes from public CDN
    const fallbackClasses = iconState.fallbackClass.split(' ');
    fallbackClasses.forEach(className => {
      if (className.trim()) {
        element.classList.add(className.trim());
      }
    });

    // Remove any emoji-specific styles
    element.style.removeProperty('font-family');
    element.style.removeProperty('font-style');
    element.style.removeProperty('font-weight');
    element.style.removeProperty('text-decoration');
    element.style.removeProperty('line-height');
    
    iconState.fallbackApplied = true;
    
    // console.log(`FontAwesome Auto Fallback: Applied fallback class "${iconState.fallbackClass}" to icon`, element);
  }

  private restoreAllIcons(): void {
    // console.log('FontAwesome Auto Fallback: Restoring all icons...');
    
    this.iconStates.forEach((iconState) => {
      this.restoreIcon(iconState);
    });
    
    // Clear the states map
    this.iconStates.clear();
    
    // Remove the public Font Awesome CDN since original CDN is working
    this.fallbackService.removePublicFontAwesome();
    
    // console.log('FontAwesome Auto Fallback: All icons restored');
  }

  private restoreIcon(iconState: IconFallbackState): void {
    if (!iconState.fallbackApplied) return;

    const element = iconState.element;
    
    // Restore original classes
    element.className = '';
    iconState.originalClasses.forEach(className => {
      element.classList.add(className);
    });
    
    // Remove fallback class
    element.classList.remove('fa-fallback-applied');
    
    // Restore original content
    element.innerHTML = iconState.originalContent;
    
    // Remove any inline styles that might have been applied
    element.style.removeProperty('font-family');
    element.style.removeProperty('font-style');
    element.style.removeProperty('font-weight');
    element.style.removeProperty('text-decoration');
    element.style.removeProperty('line-height');
    
    iconState.fallbackApplied = false;
  }

  /**
   * Manually trigger fallback for all icons (useful for testing)
   */
  public async forceApplyFallbacks(): Promise<void> {
    await this.loadFallbackAndApply();
  }

  /**
   * Manually restore all icons (useful for testing)
   */
  public forceRestoreIcons(): void {
    this.restoreAllIcons();
  }

  /**
   * Get current number of icons being managed
   */
  public getIconCount(): number {
    return this.iconStates.size;
  }
}
